<template>
    <BlockContainer
        :id="'product-view-' + slugify(product.title)"
        :background-color="blockSettings.backgroundColor"
        :bottom-angle="blockSettings.bottomAngle"
        :angle-background-color="blockSettings.nextBlockBgColor"
        :arrow-callout="blockSettings.arrowCallout"
        divider-arrow-class="left-[50%] bottom-6"
        slot-class="mt-14 pb-28"
    >
        <div class="md:flex">
            <div class="md:w-1/2 md:pr-8 xl:pr-14">
                <ProductViewCarousel ref="carouselRef" :product="product" />

                <ProductViewRelatedItems
                    v-if="product.relatedItems.length"
                    :related-items="product.relatedItems"
                    class="mt-12 hidden md:block"
                />
            </div>

            <div class="mt-12 md:mt-0 md:w-1/2 md:pl-8 xl:pl-14">
                <ProductViewDetails :product="product" class="mb-12" />

                <template v-if="useConfigurator">
                    <Alert
                        v-if="
                            selectedProductData &&
                            !selectedProductData.isInStock
                        "
                        type="warning"
                        class="mb-8"
                    >
                        This product is out of stock.
                    </Alert>

                    <ProductViewConfigurator
                        v-if="product.configurator"
                        :key="configuratorKey"
                        :product="product"
                        :product-type="productType"
                        :controls="controls"
                        :selections="selections"
                        :show-errors="showErrors"
                        :validation-messages="validationMessages"
                        :on-button-click="onButtonClick"
                        :custom-product-page-url="product.linkedProductUrl"
                        @dismiss-errors="showErrors = false"
                        @update:custom-size="customSize = $event"
                    />

                    <ProductViewContentsSelect
                        v-if="showContentsSelect"
                        v-model="contents"
                        class="mt-12"
                        :product-datas="productDatas"
                        :show-errors="showErrors"
                    />

                    <ProductViewGoToQuote
                        v-if="
                            isCustomPrintedProduct &&
                            (!selectedProductData ||
                                selectedProductData.isInStock)
                        "
                        class="mt-12"
                        :product-datas="productDatas"
                        :product-id="selectedProductData?.id || null"
                        :contents="contents"
                        :shape="goToQuoteShape"
                        :show-errors="showErrors"
                        :turnaround-time="customPrintedTurnaroundTime"
                        :validation-messages="validationMessages"
                        @show-errors="showErrors = $event"
                    />
                    <ProductViewGoToDesignLab
                        v-if="isDesignLabProduct"
                        class="mt-12"
                        :product-type="productType"
                        :product-datas="productDatas"
                        :selected-product="selectedProductData"
                        :validation-messages="validationMessages"
                        @show-errors="showErrors = $event"
                    />
                    <ProductViewAddToCart
                        v-if="
                            isStockedProduct &&
                            (!selectedProductData ||
                                selectedProductData.isInStock)
                        "
                        :key="cartKey"
                        class="mt-12"
                        :product-datas="filteredProductDatas"
                        :selected-product-data="selectedProductData"
                        :add-to-cart-image="addToCartImage"
                        :validation-messages="validationMessages"
                        @show-errors="showErrors = $event"
                    />
                </template>
                <CustomPrintedRollstock v-else />
            </div>
        </div>

        <ProductViewAdditionalDetails
            id="additional-details"
            :product="product"
            :selected-product-data="selectedProductData"
            :selected-variation="selectedVariation"
            :pricing-tiers="pricingTiers"
            class="mt-12 md:clear-both md:mt-14"
        />

        <ProductViewRelatedItems
            v-if="product.relatedItems.length"
            :related-items="product.relatedItems"
            class="mt-12 md:hidden"
        />
    </BlockContainer>
</template>

<script setup lang="ts">
import { Product, ProductData, ProductVariation } from '~/types/product';
import { BlockSettings, PricingTier } from '~/types/miscellaneous';
import { scrollToTop } from '~/utils/helpers';
import ProductViewConfigurator from './components/ProductViewConfigurator.vue';
import ProductViewCarousel from './components/ProductViewCarousel.vue';
import ProductViewRelatedItems from './components/ProductViewRelatedItems.vue';
import ProductViewDetails from './components/ProductViewDetails.vue';
import ProductViewAddToCart from './components/ProductViewAddToCart.vue';
import ProductViewGoToQuote from '~/components/page-building/product-view/components/ProductViewGoToQuote.vue';
import ProductViewContentsSelect from '~/components/page-building/product-view/components/ProductViewContentsSelect.vue';
import CustomPrintedRollstock from '~/components/page-building/product-view/components/CustomPrintedRollstock.vue';
import ProductViewAdditionalDetails from '~/components/page-building/product-view/components/ProductViewAdditionalDetails.vue';
import ProductViewGoToDesignLab from '~/components/page-building/product-view/components/ProductViewGoToDesignLab.vue';

const { portalUrl } = useRuntimeConfig().public;

const props = defineProps<{
    product: Product;
    blockSettings: BlockSettings;
}>();

const carouselRef = ref<InstanceType<typeof ProductViewCarousel> | null>(null);

const productType = computed<string>(() => props.product.configurator);

const { data: productDatas } = (await useFetch(
    `${portalUrl}/api/configurator/product-data`,
    {
        query: { configurator: props.product.configurator },
        transform: (jsendData: any) => jsendData.data,
    },
)) as { data: Ref<ProductData[]> };

const {
    isStockedProduct,
    isCustomPrintedProduct,
    isDesignLabProduct,
    showContentsSelect,
    filteredProductDatas,
    selectedProductData,
    controls,
    validationMessages,
    state,
    onButtonClick,
} = await useProductViewConfigurator(productType, productDatas);

const { selections, showErrors, contents } = toRefs(state);

const configuratorKey = ref<number>(0);
const cartKey = ref<string>('');

const selectedVariation = computed<ProductVariation | null>(() => {
    if (selectedProductData.value) {
        const foundVariation = props.product.variations.find(
            (variation) => variation.id === selectedProductData.value?.id,
        );

        if (foundVariation) {
            return foundVariation;
        }
    }

    return null;
});

const goToQuoteShape = computed(() => {
    return productType.value === 'custom-printed-sticker'
        ? selections.value.shape || null
        : null;
});

const customSize = ref<{ width: number; height: number } | null>(null);
const pricingTiers = ref<PricingTier[]>([]);

watch(selectedProductData, (productData) => {
    pricingTiers.value = productData ? productData.pricingTiers : [];
});

watch(customSize, async (newSize) => {
    if (!newSize) {
        return;
    }

    const { data: newPricingTiers } = (await $fetch(
        `${portalUrl}/api/marketing/sticker-custom-size-pricing`,
        {
            query: {
                width: newSize.width,
                height: newSize.height,
            },
        },
    )) as { data: PricingTier[] };

    // Verify that by the time the request completes, we still need the data.
    if (
        'custom-printed-sticker' === productType.value &&
        !selectedProductData.value &&
        customSize.value?.width === newSize.width &&
        customSize.value?.height === newSize.height
    ) {
        pricingTiers.value = newPricingTiers;
    }
});

const { familyTurnaroundTime } = useFamilyProductDataLabels(productDatas.value);

const customPrintedTurnaroundTime = computed(() => {
    if ('custom-printed-sticker' === productType.value) {
        return familyTurnaroundTime.value;
    }

    if (selectedProductData.value) {
        return selectedProductData.value.turnaroundTime;
    }

    return null;
});

const addToCartImage = computed(() => {
    if (selectedVariation.value?.image) {
        return selectedVariation.value?.image;
    }

    if (props.product.image) {
        return props.product.image;
    }

    return null;
});

const useConfigurator = computed(() => {
    return 'custom-printed-rollstock' !== props.product.configurator;
});

watch(selectedVariation, (variation) => {
    if (carouselRef.value) {
        carouselRef.value.scrollToVariation(variation);
    }
});

onMounted(() => {
    if (carouselRef.value && selectedVariation.value) {
        carouselRef.value.scrollToVariation(selectedVariation.value);
        configuratorKey.value = selectedVariation.value.id;
        cartKey.value = selectedVariation.value.sku;
        scrollToTop(false);
    }
    if (selectedProductData.value) {
        pricingTiers.value = selectedProductData.value.pricingTiers;
    }
});
</script>
